import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLogin, loginDestination } from '@/hooks/useAuth';
import PasswordAuthForm, {
  MethodPicker,
  type AuthMethodChoice,
} from './PasswordAuthForm';
import { supportsPasskeys } from '@/api/adapter/session';
import { Button } from '@/components/ui/Button';
import type { UserRole } from '@/types/user';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export default function LoginForm({ role }: { role: UserRole }) {
  const navigate = useNavigate();
  const mutation = useLogin();
  const { workerRole, clientRole } = useDaoConfig();
  const [method, setMethod] = useState<AuthMethodChoice>(
    supportsPasskeys() ? 'passkey' : 'password'
  );
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (values: { username: string; password?: string }) => {
    setBusy(true);
    try {
      await mutation.mutateAsync({ ...values, role });
      // Identity lookup failures must not turn a valid login into a second login attempt.
      try {
        navigate(await loginDestination(role));
      } catch {
        navigate('/dashboard');
      }
    } finally {
      mutation.reset();
      setBusy(false);
    }
  };
  return (
    <div className="onboardingContent [&_[role=tab]]:!h-auto [&_[role=tab]]:min-h-9">
      <div className="signupBtn">
        <span>Don't have an account yet?</span>
        <Button variant="secondary" onClick={() => navigate('/register')}>
          Sign up
        </Button>
      </div>
      <div className="title">
        <h1>{role === 'client' ? clientRole : workerRole} Login</h1>
        <p>
          {method === 'passkey'
            ? 'Enter your username to sign in with a passkey'
            : 'Enter your username and password'}
        </p>
      </div>
      <MethodPicker
        value={method}
        onChange={(value) => {
          setError('');
          setMethod(value);
        }}
        disabled={busy}
      />
      {!supportsPasskeys() && (
        <p className="text-center text-sm text-[--text-dark-secondary]">
          Passkeys are unavailable in this browser. Password login is available.
        </p>
      )}
      {method === 'password' ? (
        <PasswordAuthForm mode="login" role={role} onSubmit={submit} />
      ) : (
        <form
          aria-label={`login as ${role}`}
          aria-busy={busy}
          onSubmit={(event) => {
            event.preventDefault();
            setError('');
            void submit({ username }).catch((reason: unknown) =>
              setError(
                reason instanceof Error ? reason.message : 'Login failed.'
              )
            );
          }}
        >
          <div className="inputGroup">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              name="username"
              placeholder="your_username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
              disabled={busy}
            />
          </div>
          {error && (
            <p className="text-sm text-red-500" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={busy || !supportsPasskeys()}>
            {busy ? 'Authenticating…' : 'Login'}
          </Button>
        </form>
      )}
      <div className="text-center">
        <Link
          className="text-sm text-[--text-dark-secondary] hover:underline"
          to="/login"
        >
          Back to role selection
        </Link>
      </div>
    </div>
  );
}
